<template>
  <div class="user-info-tab pa-4">
    <BaseInfoSection
      :user-id="user.id"
      :user-name="user.name"
      :user-gender="daytimeAdviceSheet.userGender"
      :last-due-date="daytimeAdviceSheet.lastDueDate"
      :coupon="displayCoupon"
      :coupon-id="displayCouponId"
    />

    <LegacyUserNoticeSections
      v-if="legacyUserNoticesEnabled"
      :user="user"
      :user-note-editable="true"
      :can-edit-user-note="canEditNotes"
      @saved="fetchUserContext"
    />
    <NoteSection
      v-else
      title="ユーザーの特記事項"
      :content="specialNote"
      :editable="true"
      :can-edit="canEditNotes"
      :is-editing="isEditingUserNote"
      :draft="userNoticeDraft"
      :is-saving="userNoteSaving"
      @toggle-edit="toggleUserNoteEdit"
      @update:draft="userNoticeDraft = $event"
      @cancel-edit="cancelUserNoteEdit"
    />

    <ChildInfoSection
      :children="children"
      :loading="childrenLoading"
      :is-editing="isEditingChildren"
      :drafts="childNoticeDrafts"
      :can-edit="canEditNotes"
      :is-saving="childNoteSaving"
      @start-edit="startChildNoteEdit"
      @save-edit="saveChildNoteEdit"
      @update:drafts="childNoticeDrafts = $event"
      @cancel-edit="cancelChildNoteEdit"
    />

    <ConsultationHistorySection
      :histories="consultationHistories"
      @open="openConsultation"
    />

    <MunicipalCooperationHistorySection :histories="municipalCooperationHistories" />

    <FrequentUseResponseHistorySection :histories="frequentUseResponseHistories" />

    <HighRiskReportHistorySection :histories="highRiskReportHistories" />

    <RiskAssessmentHistorySection
      :histories="riskAssessmentHistories"
      @open="openRiskAssessment"
    />

    <EpdsHistorySection
      :histories="epdsHistories"
      @open="openEpds"
    />

    <RiskAssessmentViewDialog
      v-if="viewingRiskAssessmentId"
      :assessment-id="viewingRiskAssessmentId"
      @close="viewingRiskAssessmentId = null"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import axios from "axios";
import specialNotesApi from "@/vue/api/users/special_notes";
import faceSheetHistorySummaryApi from "@/vue/api/users/face_sheet_history_summary";
import { RiskAssessmentViewDialog } from "@/vue/features/risk-assessment-input";

import { useStore } from "vuex";
import { OPTIMISTIC_LOCK_ALERT_MESSAGE } from "@/vue/entities/face-sheet";

import BaseInfoSection from "@/vue/widgets/user-info-tab/ui/BaseInfoSection.vue";
import NoteSection from "@/vue/widgets/user-info-tab/ui/NoteSection.vue";
import LegacyUserNoticeSections from "@/vue/widgets/user-info-tab/ui/LegacyUserNoticeSections.vue";
import { FEATURE_FLAGS } from "@/vue/shared/utils/featureFlags";
import ChildInfoSection from "@/vue/widgets/user-info-tab/ui/ChildInfoSection.vue";
import ConsultationHistorySection from "@/vue/widgets/user-info-tab/ui/ConsultationHistorySection.vue";
import EpdsHistorySection from "@/vue/widgets/user-info-tab/ui/EpdsHistorySection.vue";
import MunicipalCooperationHistorySection from "@/vue/widgets/user-info-tab/ui/MunicipalCooperationHistorySection.vue";
import FrequentUseResponseHistorySection from "@/vue/widgets/user-info-tab/ui/FrequentUseResponseHistorySection.vue";
import HighRiskReportHistorySection from "@/vue/widgets/user-info-tab/ui/HighRiskReportHistorySection.vue";
import RiskAssessmentHistorySection from "@/vue/widgets/user-info-tab/ui/RiskAssessmentHistorySection.vue";

const props = defineProps({
  daytimeAdviceSheet: { type: Object, required: true },
});

const store = useStore();

// template内でリアクティブに参照するためcomputedで包む
const daytimeAdviceSheet = computed(() => props.daytimeAdviceSheet);

const cancelToken = axios.CancelToken;
let cancelSource = null;
// fetchChildContext は cancelSource（axios）の対象外で並行実行されるため、
// リクエストごとに世代IDを発行し、古い世代のレスポンスが新しい世代の結果を上書きしないようにする
let childRequestId = 0;

// ローカルstate（Vuex不使用）
const user = ref({});
const userKartes = ref([]);
const userAnytimeConsultations = ref([]);
const userDaytimeAdviceSheets = ref([]);
const epdsAnswerList = ref([]);
const municipalCooperationHistories = ref([]);
const frequentUseResponseHistories = ref([]);
const highRiskReportHistories = ref([]);
const riskAssessmentHistories = ref([]);

const isEditingUserNote = ref(false);
const userNoticeDraft = ref("");
const userNoteSaving = ref(false);
const specialNote = ref("");
const specialNoteRevisionId = ref(null);
const children = ref([]);
const childrenLoading = ref(false);
const isEditingChildren = ref(false);
const childNoticeDrafts = ref({});
const childNoteSaving = ref(false);

// --- 派生computed ---

const displayCoupon = computed(() => {
  if (user.value.coupon && user.value.coupon !== "") {
    return user.value.coupon;
  }
  if (props.daytimeAdviceSheet.couponName && props.daytimeAdviceSheet.couponName !== "") {
    return props.daytimeAdviceSheet.couponName;
  }
  return "-";
});

const displayCouponId = computed(() => {
  if (user.value.couponId !== null && user.value.couponId !== undefined && user.value.couponId !== "") {
    return user.value.couponId;
  }
  if (props.daytimeAdviceSheet.couponId !== null
    && props.daytimeAdviceSheet.couponId !== undefined
    && props.daytimeAdviceSheet.couponId !== "") {
    return props.daytimeAdviceSheet.couponId;
  }
  return "";
});

const canEditNotes = computed(() => {
  const hasAllCapabilities = store.getters.hasAllCapabilities;
  if (typeof hasAllCapabilities !== "function") return false;
  return hasAllCapabilities(["counselor:access"])
    || hasAllCapabilities(["operator:access"])
    || hasAllCapabilities(["high_risk:access"]);
});

const legacyUserNoticesEnabled = computed(() => store.getters.isFeatureEnabled?.(FEATURE_FLAGS.legacyUserNotices) === true);

// 夜間相談はバックエンドでenumが定義されていない 数値と文字列の両方に対応するラベルを定義しておく
const DEPARTMENT_LABELS = {
  1: "小児科医",
  2: "産婦人科医",
  3: "助産師",
  pediatrician: "小児科医",
  obstetrician: "産婦人科医",
  midwife: "助産師",
};

const departmentLabel = (departmentType) => DEPARTMENT_LABELS[departmentType] || departmentType || "-";

const toTimestamp = (datetime) => {
  if (!datetime) return 0;
  if (typeof datetime === "number") return datetime;

  const normalized = typeof datetime === "string"
    ? datetime.trim().replace(" ", "T")
    : datetime;
  const parsed = Date.parse(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const consultationHistories = computed(() => {
  const daytimeSheets = Array.isArray(userDaytimeAdviceSheets.value) ? userDaytimeAdviceSheets.value : [];
  const anytimeConsultations = Array.isArray(userAnytimeConsultations.value) ? userAnytimeConsultations.value : [];
  const nightKartes = Array.isArray(userKartes.value) ? userKartes.value : [];

  const daytimeHistories = daytimeSheets.map((sheet) => ({
    key: `daytime-${sheet.id}`,
    sourceType: "daytime",
    id: sheet.id,
    datetime: sheet.consultedAt || [sheet.adviceDate, sheet.startAt].filter(Boolean).join(" "),
    // 日中助産師相談は子と紐づく手段がないため、対象の子は常にブランク表示にする
    childName: null,
    childId: null,
    consultationType: `日中助産師相談 / ${departmentLabel(sheet.departmentType)}`,
    complaint: sheet.complaint,
  }));

  const anytimeHistories = anytimeConsultations.map((consultation) => ({
    key: `anytime-${consultation.id}`,
    sourceType: "anytime",
    id: consultation.id,
    datetime: consultation.consultedAt || consultation.simpleAskedAt,
    childName: consultation.childName,
    childId: consultation.childId ?? null,
    consultationType: `いつでも相談 / ${departmentLabel(consultation.departmentType)}`,
    complaint: consultation.complaint,
  }));

  const nightHistories = nightKartes.map((karte) => ({
    key: `night-${karte.id}`,
    sourceType: "night",
    id: karte.id,
    reservationId: karte.reservationId,
    datetime: karte.consultedAt || [karte.date, karte.time].filter(Boolean).join(" "),
    childName: karte.childName,
    childId: karte.childId ?? karte.child_id ?? null,
    consultationType: `夜間相談 / ${departmentLabel(karte.departmentType)}`,
    complaint: karte.complaint,
  }));

  return [...daytimeHistories, ...anytimeHistories, ...nightHistories].sort((a, b) => {
    const left = toTimestamp(a.datetime);
    const right = toTimestamp(b.datetime);
    return right - left;
  });
});

const epdsHistories = computed(() => {
  const answers = Array.isArray(epdsAnswerList.value) ? epdsAnswerList.value : [];
  return answers.map((answer) => {
    const question10 = (answer.important_questions || []).find((q) => q.question_no === 10);
    return {
      id: answer.id,
      answeredAt: answer.answeredAt || answer.answered_at,
      period: answer.weeks_after_childbirth != null ? `産後${answer.weeks_after_childbirth}` : "-",
      totalScore: answer.total_score != null ? `${answer.total_score}点` : "-",
      positive: answer.positive === true,
      question10Score: question10 ? `${question10.score}点` : "-",
      question10Choice: question10 ? question10.choice : "",
    };
  });
});

// --- API呼び出し（axios直接） ---

const fetchUserContext = async () => {
  const userId = props.daytimeAdviceSheet.userId;

  if (!userId) {
    clear();
    return;
  }

  municipalCooperationHistories.value = [];
  frequentUseResponseHistories.value = [];
  highRiskReportHistories.value = [];
  riskAssessmentHistories.value = [];

  if (cancelSource) {
    cancelSource.cancel();
  }
  cancelSource = cancelToken.source();
  const token = cancelSource.token;

  await Promise.allSettled([
    axios.get("/api/v1/users", { params: { user_id: userId }, cancelToken: token })
      .then((r) => {
        user.value = r.data || {};
      }),

    specialNotesApi.fetchSpecialNote(String(userId), { cancelToken: token })
      .then((r) => {
        specialNote.value = r.specialNote || "";
        specialNoteRevisionId.value = r.specialNoteRevisionId || null;
      }),

    axios.get(`/api/v1/users/${userId}/kartes`, {
      cancelToken: token,
      validateStatus: (s) => s < 500,
    }).then((r) => { userKartes.value = r.status === 404 ? [] : (r.data || []); }),

    axios.get(`/api/v1/users/${userId}/anytime_consultations`, { cancelToken: token })
      .then((r) => { userAnytimeConsultations.value = r.data || []; }),

    axios.get("/api/v1/daytime_advice_sheets/users_advice_sheets", {
      params: { user_id: userId },
      cancelToken: token,
    }).then((r) => { userDaytimeAdviceSheets.value = r.data || []; }),

    axios.get("/api/v1/epds_answers", { params: { user_id: userId }, cancelToken: token })
      .then((r) => { epdsAnswerList.value = r.data || []; }),

    faceSheetHistorySummaryApi.fetchFaceSheetHistorySummary(String(userId), { cancelToken: token })
      .then((r) => {
        municipalCooperationHistories.value = r.municipalCooperationHistories || [];
        frequentUseResponseHistories.value = r.frequentUseResponseHistories || [];
        highRiskReportHistories.value = r.highRiskReportHistories || [];
        riskAssessmentHistories.value = r.riskAssessmentHistories || [];
      }),
  ]);
};

const fetchChildContext = async () => {
  const requestId = ++childRequestId;
  const userId = props.daytimeAdviceSheet.userId;
  if (!userId) {
    children.value = [];
    return;
  }
  childrenLoading.value = true;
  try {
    const r = await axios.get("/api/v1/children", {
      params: { user_id: userId },
      validateStatus: (s) => s < 500,
    });
    // 取得中に別のシートへ遷移し、より新しいリクエストが発行されていた場合は
    // このレスポンスを反映しない（stale response破棄）
    if (requestId !== childRequestId) return;
    children.value = Array.isArray(r.data) ? r.data : [];
  } finally {
    if (requestId === childRequestId) {
      childrenLoading.value = false;
    }
  }
};

// --- 公開メソッド ---

// 子の情報は他のユーザー関連情報（相談履歴等）と依存関係がないため、
// fetchUserContext の完了を待たずに並行して取得し、表示までの待ち時間を短縮する。
const start = async () => {
  await Promise.allSettled([fetchUserContext(), fetchChildContext()]);
};

const clear = () => {
  if (cancelSource) {
    cancelSource.cancel();
    cancelSource = null;
  }
  // 進行中の子情報取得リクエストがあれば、その結果を無効化する
  childRequestId += 1;
  user.value = {};
  specialNote.value = "";
  specialNoteRevisionId.value = null;
  userNoteSaving.value = false;
  children.value = [];
  childrenLoading.value = false;
  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
  childNoteSaving.value = false;
  userKartes.value = [];
  userAnytimeConsultations.value = [];
  userDaytimeAdviceSheets.value = [];
  epdsAnswerList.value = [];
  municipalCooperationHistories.value = [];
  frequentUseResponseHistories.value = [];
  highRiskReportHistories.value = [];
  riskAssessmentHistories.value = [];
  // 別シートへの遷移時に前ユーザーのアセスメント閲覧ダイアログを持ち越さない
  viewingRiskAssessmentId.value = null;
};

// --- 編集操作 ---

// サイドバーは相談を切り替えてもこのタブを使い回すため、前ユーザーの下書きを別ユーザーへ保存させない
watch(() => user.value.id, () => {
  isEditingUserNote.value = false;
  userNoticeDraft.value = "";
  userNoteSaving.value = false;
});

const toggleUserNoteEdit = async () => {
  if (!canEditNotes.value) return;

  if (!isEditingUserNote.value) {
    userNoticeDraft.value = specialNote.value;
    isEditingUserNote.value = true;
    return;
  }

  const userId = user.value.id;
  // 保存中に別ユーザーへ切り替わった場合、完了後の状態更新を切替後のユーザーに反映しない
  const isStale = () => user.value.id !== userId;

  userNoteSaving.value = true;
  try {
    await specialNotesApi.saveSpecialNote(String(userId), userNoticeDraft.value, specialNoteRevisionId.value);
  } catch (e) {
    if (isStale()) return;
    if (e.response?.status === 409) {
      alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
      isEditingUserNote.value = false;
      await fetchUserContext();
      return;
    }
    throw e;
  } finally {
    if (!isStale()) userNoteSaving.value = false;
  }

  if (isStale()) return;
  isEditingUserNote.value = false;
  await fetchUserContext();
};

const startChildNoteEdit = () => {
  if (!canEditNotes.value) return;
  const drafts = {};
  children.value.forEach((c) => {
    drafts[c.id] = c.notice || "";
  });
  childNoticeDrafts.value = drafts;
  isEditingChildren.value = true;
};

const saveChildNoteEdit = async () => {
  if (!isEditingChildren.value) return;

  // 変更のあった子だけPUT
  const changed = children.value.filter((c) => {
    const draft = childNoticeDrafts.value[c.id] ?? c.notice ?? "";
    return draft !== (c.notice || "");
  });

  if (changed.length === 0) {
    isEditingChildren.value = false;
    childNoticeDrafts.value = {};
    return;
  }

  childNoteSaving.value = true;
  try {
    for (const child of changed) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`/api/v1/children/${child.id}`, {
          child: { notice: childNoticeDrafts.value[child.id], lock_version: child.lockVersion },
        }, { headers: { "Content-Type": "application/json" } });
      } catch (e) {
        if (e.response?.status === 409) {
          alert(OPTIMISTIC_LOCK_ALERT_MESSAGE);
          childNoteSaving.value = false;
          isEditingChildren.value = false;
          childNoticeDrafts.value = {};
          await fetchChildContext();
          return;
        }
        throw e;
      }
    }
  } finally {
    childNoteSaving.value = false;
  }

  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
  await fetchChildContext();
};

// --- キャンセル処理 ---

const cancelUserNoteEdit = () => {
  isEditingUserNote.value = false;
  userNoticeDraft.value = "";
};

const cancelChildNoteEdit = () => {
  isEditingChildren.value = false;
  childNoticeDrafts.value = {};
};

// --- ウィンドウ開く ---

const openConsultation = (history) => {
  if (history.sourceType === "daytime") {
    window.open(`/daytime_advices_sheets/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.sourceType === "anytime") {
    window.open(`/anytime_consultations/view/${history.id}`, "", "noopener,noreferrer,width=480,height=640");
    return;
  }
  if (history.reservationId) {
    window.open(`/reservations/${history.reservationId}`, "", "noopener,noreferrer,width=480,height=640");
  }
};

const openEpds = (id) => {
  window.open(`/epds/${id}`, "", "noopener,noreferrer,width=480,height=640");
};

const viewingRiskAssessmentId = ref(null);

const openRiskAssessment = (history) => {
  viewingRiskAssessmentId.value = history.id;
};

defineExpose({
  start,
  clear,
});
</script>

<style lang="scss" scoped>
.user-info-tab {
  background-color: #f7f7f7;
  min-height: calc(100vh - 150px);
}
</style>
